
import React, { useState, useMemo, useEffect, useRef } from 'react';
import type { Recipe, DayOfWeek, MealType, Language, WeekStartDay } from '../types';
import XIcon from './icons/XIcon';
import PlusIcon from './icons/PlusIcon';
import MinusIcon from './icons/MinusIcon';
import CartIcon from './icons/CartIcon';
import HouseholdIcon from './icons/HouseholdIcon';
import SnowflakeIcon from './icons/SnowflakeIcon';
import MeatIcon from './icons/MeatIcon';
import UtensilsIcon from './icons/UtensilsIcon';
import CalendarDaysIcon from './icons/CalendarDaysIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import TrashIcon from './icons/TrashIcon';
import ListIcon from './icons/ListIcon';
import SmartUnitCapsule from './SmartUnitCapsule';
import CalendarPicker from './CalendarPicker';
import ConfirmationModal from './ConfirmationModal';
import RecipeIngredientsModal from './RecipeIngredientsModal';
import { getTranslation, isRTL } from '../utils/translations';
import { calculateScaledMeatGrams } from '../utils/meatCalculator';
import { DAYS_OF_WEEK, DAY_TRANSLATIONS } from '../types';

interface AddMealModalProps {
    isOpen: boolean;
    onClose: () => void;
    recipe: Recipe;
    initialDay: DayOfWeek;
    initialType: MealType;
    weekOffset: number;
    initialServings?: number;
    initialStatus?: 'buy' | 'pantry' | 'prepared';
    isEditing?: boolean;
    readOnlySchedule?: boolean;
    onConfirm: (recipeId: string, day: DayOfWeek, type: MealType, servings: number, status: 'buy' | 'pantry' | 'prepared', weekOffset: number, isUpdate?: boolean) => Promise<void> | void;
    onDelete?: () => void;
    onViewRecipe?: (recipe: Recipe, servings: number) => void;
    language?: Language;
    orderedDays: DayOfWeek[];
    visibleMealTypes: MealType[];
}

const AddMealModal: React.FC<AddMealModalProps> = ({
    isOpen,
    onClose,
    recipe,
    initialDay,
    initialType,
    weekOffset,
    initialServings,
    initialStatus,
    isEditing = false,
    readOnlySchedule = false,
    onConfirm,
    onDelete,
    onViewRecipe,
    language,
    orderedDays,
    visibleMealTypes
}) => {
    const [servings, setServings] = useState(initialServings || recipe.servings || 1);
    const [status, setStatus] = useState<'buy' | 'pantry' | 'prepared'>(initialStatus || (recipe.ingredients.some(i => i.isMeat) ? 'buy' : 'buy'));
    const [selectedDay, setSelectedDay] = useState<DayOfWeek>(initialDay);
    const [selectedType, setSelectedType] = useState<MealType>(initialType);
    
    // New State for Duration/Repeat
    const [durationValue, setDurationValue] = useState(1);
    const [durationUnit, setDurationUnit] = useState<'days' | 'weeks'>('days');
    
    const [localWeekOffset, setLocalWeekOffset] = useState(weekOffset);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showIngredients, setShowIngredients] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);

    // Re-initialize only when the underlying meal/props actually change, NOT when the modal
    // is merely toggled open/closed (e.g. hidden behind the recipe preview). This preserves
    // the user's in-progress edits when they open and close the recipe preview.
    const initKeyRef = useRef<string>('');
    useEffect(() => {
        const initKey = `${recipe.id}|${initialDay}|${initialType}|${weekOffset}|${initialServings ?? ''}|${initialStatus ?? ''}`;
        if (initKeyRef.current === initKey) return;
        initKeyRef.current = initKey;
        setServings(initialServings || recipe.servings || 1);
        setStatus(initialStatus || 'buy');
        setSelectedDay(initialDay);
        setSelectedType(initialType);
        setLocalWeekOffset(weekOffset);
        setDurationValue(1);
        setIsSaving(false);
    }, [recipe.id, recipe.servings, initialServings, initialStatus, initialDay, initialType, weekOffset]);

    const currentWeekStart = useMemo(() => {
        const startDayOfWeek = orderedDays[0];
        const now = new Date();
        const currentDay = now.getDay();
        const startDayIndex = startDayOfWeek === 'Sunday' ? 0 : 1;
        let diff = currentDay - startDayIndex;
        if (startDayOfWeek === 'Monday' && currentDay === 0) diff = 6; 
        else if (diff < 0) diff += 7;
        
        const d = new Date(now);
        d.setDate(now.getDate() - diff);
        d.setHours(0,0,0,0);
        return d;
    }, [orderedDays]);

    const weekStartDay: WeekStartDay = orderedDays[0] === 'Sunday' ? 'Sunday' : 'Monday';

    const currentDate = useMemo(() => {
        const dayIndex = orderedDays.indexOf(selectedDay);
        const d = new Date(currentWeekStart);
        d.setDate(currentWeekStart.getDate() + (localWeekOffset * 7) + dayIndex);
        return d;
    }, [selectedDay, localWeekOffset, currentWeekStart, orderedDays]);

    const repeatCount = useMemo(
        () => (isEditing ? 1 : Math.max(1, durationValue)),
        [isEditing, durationValue],
    );

    const meatWeight = useMemo(() => {
        return calculateScaledMeatGrams(recipe, servings, repeatCount);
    }, [recipe, servings, repeatCount]);

    const totalPlannedServings = useMemo(
        () => servings * repeatCount,
        [servings, repeatCount],
    );

    if (!isOpen) return null;

    const handleDateSelection = (newDate: Date) => {
        newDate.setHours(0,0,0,0);
        const timeDiff = newDate.getTime() - currentWeekStart.getTime();
        const daysDiff = Math.round(timeDiff / (1000 * 3600 * 24));
        const newOffset = Math.floor(daysDiff / 7);
        let newDayIndex = daysDiff % 7;
        if (newDayIndex < 0) newDayIndex += 7;
        setLocalWeekOffset(newOffset);
        setSelectedDay(orderedDays[newDayIndex]);
    };

    const handleConfirm = async () => {
        setIsSaving(true);
        const scheduleLocked = readOnlySchedule && isEditing;
        const start = new Date(currentDate);
        const appWeekStart = currentWeekStart;

        try {
            for (let i = 0; i < durationValue; i++) {
                const targetDate = new Date(start);

                if (durationUnit === 'days') {
                    targetDate.setDate(start.getDate() + i);
                } else {
                    targetDate.setDate(start.getDate() + (i * 7));
                }

                const dayName = scheduleLocked ? initialDay : DAYS_OF_WEEK[targetDate.getDay()];

                const diffTime = targetDate.getTime() - appWeekStart.getTime();
                const diffWeeks = scheduleLocked
                    ? weekOffset
                    : Math.floor(Math.round(diffTime / (1000 * 60 * 60 * 24)) / 7);

                const isUpdateIteration = isEditing && i === 0;
                const confirmType = scheduleLocked ? initialType : selectedType;

                await onConfirm(recipe.id, dayName, confirmType, servings, status, diffWeeks, isUpdateIteration);
            }
            setIsSaving(false);
            onClose();
        } catch (error) {
            setIsSaving(false);
            console.error("Meal scheduling error:", error);
        }
    };

    const handleDelete = () => {
        if (onDelete) onDelete();
        onClose();
    };

    const sourcingOptions = [
        { id: 'buy', icon: <CartIcon className="w-4 h-4" />, label: t('sourcing_buy') },
        { id: 'pantry', icon: <HouseholdIcon className="w-4 h-4" />, label: t('sourcing_pantry') },
        { id: 'prepared', icon: <SnowflakeIcon className="w-4 h-4" />, label: t('sourcing_prepared') },
    ];

    const dateOptions: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };
    const formattedDate = currentDate.toLocaleDateString(language, dateOptions);

    // Helper text logic
    const getScheduleHint = () => {
        if (durationValue <= 1) return null;
        
        if (durationUnit === 'days') {
            return t('schedule_days_hint').replace('{count}', durationValue.toString());
        } else {
            const dayName = t('day_' + selectedDay);
            return t('schedule_weeks_hint').replace('{day}', dayName).replace('{count}', durationValue.toString());
        }
    };

    const displayMeat = () => {
        if (meatWeight >= 1000) {
            return `${parseFloat((meatWeight / 1000).toFixed(2))} ${t('unit_kg')}`;
        }
        return `${Math.round(meatWeight)} ${t('unit_g')}`;
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-sm overflow-hidden border border-white/20 dark:border-slate-800 transition-colors" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="p-6 bg-slate-50/50 dark:bg-slate-800/50 border-b dark:border-slate-800 flex justify-between items-center">
                    <div className="flex-grow min-w-0">
                        <h3 className="font-black text-slate-800 dark:text-slate-100 tracking-tight text-lg truncate pr-4">
                            {isEditing ? t('editMeal') : t('schedule')}
                        </h3>
                        <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest truncate">{recipe.name}</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400 transition-colors shrink-0">
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6">
                    {/* Section 1: Schedule Group */}
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-2 border border-slate-100 dark:border-slate-800 mb-6 pb-3">
                        {/* Row 1: Date & Time */}
                        <div className="flex flex-row gap-2 mb-2">
                            {readOnlySchedule && isEditing ? (
                                <div className="flex-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg h-10 flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400 font-medium text-sm">
                                    <CalendarDaysIcon className="w-4 h-4 text-slate-400" />
                                    <span>{formattedDate}</span>
                                </div>
                            ) : (
                                <button 
                                    type="button"
                                    onClick={() => setIsCalendarOpen(true)}
                                    className="flex-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg h-10 flex items-center justify-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all text-slate-700 dark:text-slate-200 font-medium text-sm"
                                >
                                    <CalendarDaysIcon className="w-4 h-4 text-slate-400" />
                                    <span>{formattedDate}</span>
                                </button>
                            )}
                            
                            {readOnlySchedule && isEditing ? (
                                <div className="w-1/3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg h-10 flex items-center justify-center text-slate-500 dark:text-slate-400 font-medium text-sm">
                                    {t(selectedType === 'Breakfast' ? 'morning' : selectedType === 'Lunch' ? 'noon' : 'evening')}
                                </div>
                            ) : (
                                <div className="w-1/3 relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg h-10 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-700 transition-all">
                                    <select
                                        value={selectedType}
                                        onChange={(e) => setSelectedType(e.target.value as MealType)}
                                        className="appearance-none bg-transparent border-none w-full h-full text-center text-sm font-medium text-slate-700 dark:text-slate-200 focus:ring-0 cursor-pointer pl-2 pr-6 outline-none"
                                    >
                                        {visibleMealTypes.map(type => (
                                            <option key={type} value={type} className="bg-white dark:bg-slate-900">{t(type === 'Breakfast' ? 'morning' : type === 'Lunch' ? 'noon' : 'evening')}</option>
                                        ))}
                                    </select>
                                    <ChevronDownIcon className="w-3 h-3 text-slate-400 absolute ltr:right-2 rtl:left-2 pointer-events-none" />
                                </div>
                            )}
                        </div>

                        {/* Row 2: Duration / Repeat (scheduling only — hidden when editing an existing meal) */}
                        {!isEditing && !readOnlySchedule && (
                        <div className="flex flex-col gap-1">
                            <div className="flex flex-row items-center justify-between px-3 h-12 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
                                <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">{t('duration_repeat')}</span>
                                <SmartUnitCapsule 
                                    value={durationValue} 
                                    unit={durationUnit} 
                                    onChange={(v, u) => {
                                        setDurationValue(v);
                                        if (u === 'days' || u === 'weeks') {
                                            setDurationUnit(u as 'days' | 'weeks');
                                        }
                                    }} 
                                    availableUnits={['days', 'weeks']}
                                    language={language}
                                    step={1}
                                    min={1}
                                    bgClass="bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 min-w-[7rem]"
                                />
                            </div>
                            {/* Smart Hint Text */}
                            {getScheduleHint() && (
                                <p className="text-xs text-brand-sage dark:text-brand-sage/80 font-bold px-2 animate-fade-in text-center">
                                    {getScheduleHint()}
                                </p>
                            )}
                        </div>
                        )}
                    </div>

                    {/* Section 2: Yield / Servings */}
                    <div className="flex flex-row items-center gap-3 w-full justify-start px-1 mb-6">
                        <span className="text-lg font-bold text-slate-800 dark:text-slate-100">
                            {repeatCount > 1 ? t('servingsPerMeal') : t('quantityToMake')}
                        </span>
                        <SmartUnitCapsule 
                            value={servings} 
                            unit="servings" 
                            onChange={(v, _u) => setServings(v)} 
                            availableUnits={['servings']}
                            language={language}
                            step={1}
                            min={1}
                            hideUnit={true}
                            bgClass="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm min-w-[5rem]"
                        />
                        {repeatCount > 1 && (
                            <span className="flex flex-row items-center justify-center px-3 h-9 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-inner select-none pointer-events-none ms-auto">
                                <span className="text-slate-400 dark:text-slate-500 font-medium me-1.5 text-xs uppercase tracking-wider">{t('total')}</span>
                                <span className="text-slate-700 dark:text-slate-200 font-bold text-base">{totalPlannedServings}</span>
                            </span>
                        )}
                    </div>

                    {/* Section 3: Sourcing (Segmented Control) */}
                    <div className="flex p-1 bg-slate-100 dark:bg-slate-800 rounded-xl mb-6 h-10">
                        {sourcingOptions.map(opt => (
                            <button
                                key={opt.id}
                                onClick={() => setStatus(opt.id as any)}
                                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg text-[10px] font-black uppercase tracking-wide transition-all ${status === opt.id ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}
                            >
                                {opt.icon}
                                <span className="truncate">{opt.label}</span>
                            </button>
                        ))}
                    </div>

                    {meatWeight > 0 && (
                        <div className="flex items-center justify-center gap-2 py-2 bg-rose-50/50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800/30 rounded-xl mb-4">
                            <MeatIcon className="w-4 h-4 text-rose-500" />
                            <span className="text-xs font-black text-rose-600 dark:text-rose-400 uppercase tracking-tight">
                                {displayMeat()} {t('meat')}
                            </span>
                        </div>
                    )}

                    <div className="space-y-3">
                        {onViewRecipe && (
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => onViewRecipe(recipe, servings)}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 font-black rounded-xl text-[10px] uppercase tracking-widest hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-all border border-sky-100 dark:border-sky-800"
                                >
                                    <UtensilsIcon className="w-3.5 h-3.5 shrink-0" />
                                    {t('viewRecipe')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowIngredients(true)}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-black rounded-xl text-[10px] uppercase tracking-widest hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all border border-emerald-100 dark:border-emerald-800"
                                >
                                    <ListIcon className="w-3.5 h-3.5 shrink-0" />
                                    {t('viewIngredients')}
                                </button>
                            </div>
                        )}
                        
                        {isEditing && onDelete && !readOnlySchedule && (
                            <button 
                                onClick={() => setShowDeleteConfirm(true)}
                                className="w-full py-3 bg-white dark:bg-slate-800 border border-red-100 dark:border-red-900/30 text-red-500 dark:text-red-400 font-black rounded-xl text-[10px] uppercase tracking-widest hover:bg-red-50 dark:hover:bg-red-900/10 transition-all flex items-center justify-center gap-2"
                            >
                                <TrashIcon className="w-3.5 h-3.5" />
                                {t('delete')}
                            </button>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800 border-t dark:border-slate-700 flex gap-3">
                    <button onClick={onClose} className="flex-1 py-3 text-slate-400 dark:text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-300 transition-colors">{t('cancel')}</button>
                    <button 
                        onClick={handleConfirm}
                        disabled={isSaving}
                        className="flex-[2] py-3 bg-brand-sage text-white font-black rounded-xl shadow-xl shadow-brand-sage/20 hover:opacity-90 transition-all text-sm uppercase tracking-widest active:scale-95 disabled:opacity-50"
                    >
                        {isSaving ? '...' : (isEditing ? t('save') : t('add'))}
                    </button>
                </div>
            </div>

            <CalendarPicker 
                isOpen={isCalendarOpen} 
                onClose={() => setIsCalendarOpen(false)} 
                selectedDate={currentDate} 
                onSelectDate={handleDateSelection}
                language={language}
                weekStartDay={weekStartDay}
            />

            <ConfirmationModal 
                isOpen={showDeleteConfirm} 
                title={t('delete')} 
                message={t('deleteMealConfirm')}
                onConfirm={() => {
                    handleDelete();
                    setShowDeleteConfirm(false);
                }}
                onCancel={() => setShowDeleteConfirm(false)}
                confirmText={t('delete')}
                cancelText={t('cancel')}
            />

            <RecipeIngredientsModal
                isOpen={showIngredients}
                onClose={() => setShowIngredients(false)}
                recipe={recipe}
                servings={totalPlannedServings}
                language={language}
            />
        </div>
    );
};

export default AddMealModal;
